import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { DateUtilsService } from 'app/services/utils';
import { catchError, concatMap, map, of, switchMap, tap } from 'rxjs';
import { VenueAdapterService } from '../../venue/services/venue-adapter.service';
import { PlaydateService } from '../services/playdate.service';
import * as PlaydateActions from './playdate.actions';

@Injectable()
export class PlaydatesEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _playdateService = inject(PlaydateService);
    private _alertService = inject(AlertService);
    private _dateUtils = inject(DateUtilsService);
    private _adapterService = inject(VenueAdapterService);

    messages = {
        getAllError: 'Error getting all playdates',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Playdates
    getAllPlaydates$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaydateActions.loadPlaydates),
            switchMap(() => {
                return this._playdateService.getAll().pipe(
                    map((res) => {
                        const playdates = res.data;
                        const sortedPlaydates = this._dateUtils.sortByDate<any>(
                            playdates,
                            'date'
                        );
                        return PlaydateActions.loadPlaydatesSuccess({
                            playdates: sortedPlaydates,
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaydateActions.loadPlaydatesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Playdate
    getPlaydate$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaydateActions.loadPlaydate),
            switchMap((action) => {
                return this._playdateService.getOne(action.id).pipe(
                    map((res) => {
                        return PlaydateActions.loadPlaydateSuccess({
                            playdate: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaydateActions.loadPlaydateFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Playdate
    getPlaydateAssociatedWithVenue$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaydateActions.loadPlaydate),
            switchMap((action) => {
                return this._playdateService.getOne(action.id).pipe(
                    map((res) => {
                        return PlaydateActions.loadPlaydateSuccess({
                            playdate: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaydateActions.loadPlaydateFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Playdate
    createPlaydate$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaydateActions.createPlaydate),
            concatMap((action) => {
                return this._playdateService.create(action.playdateData).pipe(
                    // tap((res) => {
                    //     console.log(res);
                    // }),
                    map((res) => {
                        return PlaydateActions.createPlaydateSuccess({
                            playdates: res.data,
                            success: 'Playdate(s) created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaydateActions.createPlaydateFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Playdate
    updatePlaydate$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaydateActions.updatePlaydate),
            switchMap((action) => {
                return this._playdateService
                    .update(action.playdateData, action.id)
                    .pipe(
                        map((res) => {
                            return PlaydateActions.updatePlaydateSuccess({
                                playdate: res.data,
                                success: 'Playdate updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                PlaydateActions.updatePlaydateFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Playdate
    deletePlaydate$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaydateActions.deletePlaydate),
            concatMap((action) => {
                return this._playdateService.delete(action.id).pipe(
                    map(() => {
                        return PlaydateActions.deletePlaydateSuccess({
                            id: action.id,
                            success: 'Playdate deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            PlaydateActions.deletePlaydateFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Playdate Features
    updatePlaydateFeatures$ = createEffect(() =>
        this._actions$.pipe(
            ofType(PlaydateActions.updatePlaydateFeatures),
            switchMap((action) => {
                return this._playdateService
                    .updateFeatures(action.data, action.playdateId)
                    .pipe(
                        // Debug
                        // tap((res) => {
                        //     console.log(res);
                        // }),
                        map((res) => {
                            // Adapt playdate
                            const adaptedPlaydate =
                                this._adapterService.adaptPlaydates(res.data);

                            return PlaydateActions.updatePlaydateFeaturesSuccess(
                                {
                                    playdate: adaptedPlaydate,
                                    success:
                                        'Playdate finances saved successfully!',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                PlaydateActions.updatePlaydateFeaturesFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onPlaydateActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    PlaydateActions.createPlaydateSuccess,
                    PlaydateActions.updatePlaydateSuccess,
                    PlaydateActions.deletePlaydateSuccess,
                    //
                    PlaydateActions.updatePlaydateFeaturesSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onPlaydateActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    PlaydateActions.loadPlaydatesFailure,
                    PlaydateActions.loadPlaydateFailure,
                    PlaydateActions.createPlaydateFailure,
                    PlaydateActions.updatePlaydateFailure,
                    PlaydateActions.deletePlaydateFailure,
                    //
                    PlaydateActions.updatePlaydateFeaturesFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
